import { useAlert } from 'dashboard/composables';
import SearchAPI from '../../api/search';
import types from '../mutation-types';

const requestSequence = {
  contact: 0,
  conversation: 0,
  message: 0,
  article: 0,
};

export const initialState = {
  records: [],
  contactRecords: [],
  conversationRecords: [],
  messageRecords: [],
  articleRecords: [],
  uiFlags: {
    isFetching: false,
    isSearchCompleted: false,
    contact: { isFetching: false },
    conversation: { isFetching: false },
    message: { isFetching: false },
    article: { isFetching: false },
  },
};

export const getters = {
  getConversations(state) {
    return state.records;
  },
  getContactRecords(state) {
    return state.contactRecords;
  },
  getConversationRecords(state) {
    return state.conversationRecords;
  },
  getMessageRecords(state) {
    return state.messageRecords;
  },
  getArticleRecords(state) {
    return state.articleRecords;
  },
  getUIFlags(state) {
    return state.uiFlags;
  },
};

export const actions = {
  async get({ commit }, { q }) {
    commit(types.SEARCH_CONVERSATIONS_SET, []);
    if (!q) {
      return;
    }
    commit(types.SEARCH_CONVERSATIONS_SET_UI_FLAG, { isFetching: true });
    try {
      const {
        data: { payload },
      } = await SearchAPI.get({ q });
      commit(types.SEARCH_CONVERSATIONS_SET, payload);
    } catch (error) {
      // Ignore error
    } finally {
      commit(types.SEARCH_CONVERSATIONS_SET_UI_FLAG, {
        isFetching: false,
      });
    }
  },
  // `types` is the requested result types; renamed so it does not shadow the
  // mutation-types import (which made both UI-flag commits undefined).
  async fullSearch(
    { commit, dispatch },
    { q, types: searchTypes = [], field = 'all' }
  ) {
    if (!q) {
      return;
    }
    commit(types.FULL_SEARCH_SET_UI_FLAG, {
      isFetching: true,
      isSearchCompleted: false,
    });
    try {
      const actionByType = {
        contacts: 'contactSearch',
        conversations: 'conversationSearch',
        messages: 'messageSearch',
        articles: 'articleSearch',
      };
      const requestedTypes = searchTypes.length
        ? searchTypes
        : Object.keys(actionByType);
      await Promise.all(
        requestedTypes
          .filter(type => actionByType[type])
          .map(type => dispatch(actionByType[type], { q, field }))
      );
    } catch (error) {
      // Ignore error
    } finally {
      commit(types.FULL_SEARCH_SET_UI_FLAG, {
        isFetching: false,
        isSearchCompleted: true,
      });
    }
  },
  async contactSearch({ commit }, { q, page = 1, field = 'all' }) {
    const requestId = ++requestSequence.contact;
    commit(types.CONTACT_SEARCH_SET_UI_FLAG, { isFetching: true });
    try {
      const { data } = await SearchAPI.contacts({ q, page, field });
      if (requestId !== requestSequence.contact) return false;
      commit(types.CONTACT_SEARCH_SET, {
        records: data.payload.contacts,
        page,
      });
      return true;
    } catch (error) {
      if (error.code !== 'ERR_CANCELED') throw error;
      return false;
    } finally {
      if (requestId === requestSequence.contact) {
        commit(types.CONTACT_SEARCH_SET_UI_FLAG, { isFetching: false });
      }
    }
  },
  async conversationSearch({ commit }, { q, page = 1, field = 'all' }) {
    const requestId = ++requestSequence.conversation;
    commit(types.CONVERSATION_SEARCH_SET_UI_FLAG, { isFetching: true });
    try {
      const { data } = await SearchAPI.conversations({ q, page, field });
      if (requestId !== requestSequence.conversation) return false;
      commit(types.CONVERSATION_SEARCH_SET, {
        records: data.payload.conversations,
        page,
      });
      return true;
    } catch (error) {
      if (error.code === 'ERR_CANCELED') return false;
      // The server cancelled a search that ran too long: say so instead of
      // showing an empty result.
      if (error.response?.data?.code === 'search_too_slow') {
        useAlert(error.response.data.error);
        return false;
      }
      throw error;
    } finally {
      if (requestId === requestSequence.conversation) {
        commit(types.CONVERSATION_SEARCH_SET_UI_FLAG, { isFetching: false });
      }
    }
  },
  async messageSearch({ commit }, { q, page = 1, field = 'all' }) {
    const requestId = ++requestSequence.message;
    commit(types.MESSAGE_SEARCH_SET_UI_FLAG, { isFetching: true });
    try {
      const { data } = await SearchAPI.messages({ q, page, field });
      if (requestId !== requestSequence.message) return false;
      commit(types.MESSAGE_SEARCH_SET, {
        records: data.payload.messages,
        page,
      });
      return true;
    } catch (error) {
      if (error.code !== 'ERR_CANCELED') throw error;
      return false;
    } finally {
      if (requestId === requestSequence.message) {
        commit(types.MESSAGE_SEARCH_SET_UI_FLAG, { isFetching: false });
      }
    }
  },
  async articleSearch({ commit }, { q, page = 1, field = 'all' }) {
    const requestId = ++requestSequence.article;
    commit(types.ARTICLE_SEARCH_SET_UI_FLAG, { isFetching: true });
    try {
      const { data } = await SearchAPI.articles({ q, page, field });
      if (requestId !== requestSequence.article) return false;
      commit(types.ARTICLE_SEARCH_SET, {
        records: data.payload.articles,
        page,
      });
      return true;
    } catch (error) {
      if (error.code !== 'ERR_CANCELED') throw error;
      return false;
    } finally {
      if (requestId === requestSequence.article) {
        commit(types.ARTICLE_SEARCH_SET_UI_FLAG, { isFetching: false });
      }
    }
  },
  async clearSearchResults({ commit }) {
    Object.keys(requestSequence).forEach(key => {
      requestSequence[key] += 1;
    });
    SearchAPI.cancelAll();
    commit(types.CLEAR_SEARCH_RESULTS);
  },
};

export const mutations = {
  [types.SEARCH_CONVERSATIONS_SET](state, records) {
    state.records = records;
  },
  [types.CONTACT_SEARCH_SET](state, payload) {
    const { records = [], page = 1 } = Array.isArray(payload)
      ? { records: payload, page: 1 }
      : payload || {};
    const nextRecords = Array.isArray(records) ? records : [];
    state.contactRecords =
      page === 1 ? nextRecords : [...state.contactRecords, ...nextRecords];
  },
  [types.CONVERSATION_SEARCH_SET](state, payload) {
    const { records = [], page = 1 } = Array.isArray(payload)
      ? { records: payload, page: 1 }
      : payload || {};
    const nextRecords = Array.isArray(records) ? records : [];
    state.conversationRecords =
      page === 1 ? nextRecords : [...state.conversationRecords, ...nextRecords];
  },
  [types.MESSAGE_SEARCH_SET](state, payload) {
    const { records = [], page = 1 } = Array.isArray(payload)
      ? { records: payload, page: 1 }
      : payload || {};
    const nextRecords = Array.isArray(records) ? records : [];
    state.messageRecords =
      page === 1 ? nextRecords : [...state.messageRecords, ...nextRecords];
  },
  [types.ARTICLE_SEARCH_SET](state, payload) {
    const { records = [], page = 1 } = Array.isArray(payload)
      ? { records: payload, page: 1 }
      : payload || {};
    const nextRecords = Array.isArray(records) ? records : [];
    state.articleRecords =
      page === 1 ? nextRecords : [...state.articleRecords, ...nextRecords];
  },
  [types.SEARCH_CONVERSATIONS_SET_UI_FLAG](state, uiFlags) {
    state.uiFlags = { ...state.uiFlags, ...uiFlags };
  },
  [types.FULL_SEARCH_SET_UI_FLAG](state, uiFlags) {
    state.uiFlags = { ...state.uiFlags, ...uiFlags };
  },
  [types.CONTACT_SEARCH_SET_UI_FLAG](state, uiFlags) {
    state.uiFlags.contact = { ...state.uiFlags.contact, ...uiFlags };
  },
  [types.CONVERSATION_SEARCH_SET_UI_FLAG](state, uiFlags) {
    state.uiFlags.conversation = { ...state.uiFlags.conversation, ...uiFlags };
  },
  [types.MESSAGE_SEARCH_SET_UI_FLAG](state, uiFlags) {
    state.uiFlags.message = { ...state.uiFlags.message, ...uiFlags };
  },
  [types.ARTICLE_SEARCH_SET_UI_FLAG](state, uiFlags) {
    state.uiFlags.article = { ...state.uiFlags.article, ...uiFlags };
  },
  [types.CLEAR_SEARCH_RESULTS](state) {
    state.contactRecords = [];
    state.conversationRecords = [];
    state.messageRecords = [];
    state.articleRecords = [];
  },
};

export default {
  namespaced: true,
  state: initialState,
  getters,
  actions,
  mutations,
};
